/**
 * Client-side logo processing for tenant branding. Runs entirely in a <canvas>
 * (no deps) so the server only ever stores a small, PREDICTABLE image:
 *   - load a chosen File (or an already-saved logo URL) into a decoded source
 *   - let the operator frame it (zoom / pan / background) — see LogoAdjuster
 *   - compose that framing into a SQUARE PNG blob, ≤ the API's 512 KB cap
 *   - sample the dominant/most-saturated color as a #RRGGBB hex
 *
 * ── Why square ───────────────────────────────────────────────────────────────
 * The logo is rendered at four different aspect ratios downstream (a 48px
 * circle on the kiosk masthead, a 40px rounded square on the checkout hero and
 * the QR-credit hero, a 72px box in these editors). A wide source image in a
 * square/circular frame either letterboxes to a sliver or gets center-cropped —
 * which is exactly the "logo doesn't fit and I can't fix it" failure. Storing a
 * square with the framing BAKED IN makes every downstream surface agree, and
 * makes the framing the uploader's decision instead of CSS's.
 *
 * SVGs frequently ship with a viewBox but no width/height, which rasterizes to
 * a blank canvas; loadLogoSource patches intrinsic dimensions in from the
 * viewBox so those are framable too. If a source still can't be measured we
 * fall back to Parkify purple for the color.
 *
 * Shared by the ops console and the tenant portal (`@parkify/ui/logo-brand`).
 * It was two byte-identical copies kept in step by a comment until 2026-10-22.
 */

/** A decoded, measurable logo ready to be framed. */
export interface LogoSource {
  img: HTMLImageElement
  width: number
  height: number
  /** Original bytes as a data URL — safe to re-decode, never tainted. */
  dataUrl: string
}

/** How the source is framed inside the square. Persisted only for the session. */
export interface LogoAdjust {
  /** Scale relative to "contain": 1 = the long edge exactly touches the square. */
  zoom: number
  /** Pan as a fraction (-1..1) of the available slack on each axis. */
  offsetX: number
  offsetY: number
  /** Matte behind the logo. Transparent keeps PNG alpha; white flattens it. */
  background: 'transparent' | 'white'
}

export interface ProcessedLogo {
  blob: Blob          // ready to upload — square PNG
  dataUrl: string     // instant preview (same pixels as the blob)
  brandColor: string  // detected dominant color, #RRGGBB
}

const FALLBACK = '#7F3F98' // Parkify Master Purple

// Default framing: slightly inside "contain" so a circular mask (the kiosk
// masthead) can't shave the edge off a logo that runs to its own bounding box.
export const DEFAULT_ADJUST: LogoAdjust = {
  zoom: 0.92, offsetX: 0, offsetY: 0, background: 'transparent',
}
export const MIN_ZOOM = 0.4
// Baseline zoom ceiling. Raised per-image by maxZoomFor so that "Fill frame"
// is always REACHABLE — a fixed ceiling of 3 silently under-fills any source
// wider than 3:1, which is the "logo doesn't fit and I can't fix it" bug all
// over again, just with a slider in front of it.
export const MAX_ZOOM = 3
const HARD_MAX_ZOOM = 8

// Output edge, in px. 512 is ~10x the largest surface that renders it (48px)
// so it stays crisp on a 3x tablet, and a flat-color logo encodes to well under
// the cap at that size. Photographic "logos" that don't are stepped down by
// composeLogo rather than rejected at upload.
export const OUTPUT_SIZE = 512
const STEP_DOWN = [512, 384, 256, 192]
// The API rejects > 512 KB; leave headroom so we never hand it a borderline blob.
const MAX_UPLOAD_BYTES = 480 * 1024

// ── Loading ──────────────────────────────────────────────────────────────────

function readAsDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const fr = new FileReader()
    fr.onload = () => resolve(fr.result as string)
    fr.onerror = () => reject(fr.error ?? new Error('read failed'))
    fr.readAsDataURL(blob)
  })
}

function readAsText(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const fr = new FileReader()
    fr.onload = () => resolve(String(fr.result ?? ''))
    fr.onerror = () => reject(fr.error ?? new Error('read failed'))
    fr.readAsText(blob)
  })
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error('image decode failed'))
    img.src = src
  })
}

/**
 * Give a dimensionless SVG intrinsic width/height from its viewBox. Without
 * this Chrome decodes it at 0x0 and drawImage paints nothing — the old code
 * detected that and gave up on framing (kept the raw file), which is precisely
 * the case an uploader most wants to fix.
 */
function svgWithIntrinsicSize(markup: string): string | null {
  const open = markup.match(/<svg\b[^>]*>/i)?.[0]
  if (!open) return null
  if (/\bwidth\s*=/i.test(open) && /\bheight\s*=/i.test(open)) return null
  const vb = open.match(/viewBox\s*=\s*["']\s*([-\d.eE]+)[,\s]+([-\d.eE]+)[,\s]+([-\d.eE]+)[,\s]+([-\d.eE]+)\s*["']/i)
  if (!vb) return null
  const w = Number(vb[3]), h = Number(vb[4])
  if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) return null
  const patched = open.replace(/^<svg\b/i, `<svg width="${w}" height="${h}"`)
  return markup.replace(open, patched)
}

async function toSource(blob: Blob): Promise<LogoSource> {
  let dataUrl = await readAsDataUrl(blob)
  let img = await loadImage(dataUrl)
  if ((!img.width || !img.height) && /svg/i.test(blob.type)) {
    const patched = svgWithIntrinsicSize(await readAsText(blob))
    if (patched) {
      dataUrl = `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(patched)))}`
      img = await loadImage(dataUrl)
    }
  }
  const width = img.naturalWidth || img.width
  const height = img.naturalHeight || img.height
  if (!width || !height) throw new Error('could not measure image')
  return { img, width, height, dataUrl }
}

/** Decode a user-chosen file into a framable source. Throws on unreadable input. */
export function loadLogoSource(file: File): Promise<LogoSource> {
  return toSource(file)
}

/**
 * Re-open an ALREADY-SAVED logo for reframing. Fetched as bytes (not set as an
 * <img src>) on purpose: the serve route sends `Access-Control-Allow-Origin: *`
 * with a year-long immutable cache, so a plain crossOrigin <img> can hit a
 * cache entry stored by an earlier no-CORS render and taint the canvas. Bytes →
 * data URL is same-origin by construction and can't taint.
 */
export async function loadLogoSourceFromUrl(url: string): Promise<LogoSource> {
  const res = await fetch(url, { mode: 'cors' })
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  return toSource(await res.blob())
}

// ── Framing geometry (pure — unit-tested) ────────────────────────────────────

export interface LogoLayout {
  drawW: number
  drawH: number
  dx: number
  dy: number
  /** Half the mismatch between drawn size and the square, per axis: the pan range. */
  slackX: number
  slackY: number
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v
}

/**
 * Zoom ceiling for a given source: at least MAX_ZOOM, and always a little past
 * what "Fill frame" needs, so the slider can reach (and overshoot) a full cover
 * on a wide logo. Capped so a freak 40:1 banner can't produce a useless slider.
 */
export function maxZoomFor(srcW: number, srcH: number): number {
  const short = Math.min(srcW, srcH)
  const cover = short > 0 ? Math.max(srcW, srcH) / short : 1
  return clamp(Math.max(MAX_ZOOM, cover * 1.2), MAX_ZOOM, HARD_MAX_ZOOM)
}

export function clampAdjust(a: LogoAdjust, maxZoom = MAX_ZOOM): LogoAdjust {
  return {
    zoom: clamp(a.zoom, MIN_ZOOM, maxZoom),
    offsetX: clamp(a.offsetX, -1, 1),
    offsetY: clamp(a.offsetY, -1, 1),
    background: a.background,
  }
}

/**
 * Where the source lands inside a `size`×`size` square. Offsets are normalized
 * to the slack so the SAME adjust renders identically in the 260px editor and
 * the 512px output — no separate "preview scale" to keep in sync.
 */
export function layoutLogo(srcW: number, srcH: number, size: number, adjust: LogoAdjust): LogoLayout {
  const a = clampAdjust(adjust, maxZoomFor(srcW, srcH))
  const contain = size / Math.max(srcW, srcH)
  const drawW = srcW * contain * a.zoom
  const drawH = srcH * contain * a.zoom
  const slackX = Math.abs(drawW - size) / 2
  const slackY = Math.abs(drawH - size) / 2
  return {
    drawW,
    drawH,
    dx: (size - drawW) / 2 + a.offsetX * slackX,
    dy: (size - drawH) / 2 + a.offsetY * slackY,
    slackX,
    slackY,
  }
}

/** Zoom at which the SHORT edge covers the square (no letterboxing, edges cropped). */
export function fillZoom(srcW: number, srcH: number): number {
  const short = Math.min(srcW, srcH)
  if (!short) return 1
  return clamp(Math.max(srcW, srcH) / short, MIN_ZOOM, maxZoomFor(srcW, srcH))
}

// ── Drawing ──────────────────────────────────────────────────────────────────

export function drawLogo(
  ctx: CanvasRenderingContext2D,
  src: LogoSource,
  adjust: LogoAdjust,
  size: number,
): void {
  ctx.clearRect(0, 0, size, size)
  if (adjust.background === 'white') {
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, size, size)
  }
  const l = layoutLogo(src.width, src.height, size, adjust)
  ctx.imageSmoothingEnabled = true
  ctx.imageSmoothingQuality = 'high'
  ctx.drawImage(src.img, l.dx, l.dy, l.drawW, l.drawH)
}

function squareCanvas(src: LogoSource, adjust: LogoAdjust, size: number): HTMLCanvasElement | null {
  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size
  const ctx = canvas.getContext('2d')
  if (!ctx) return null
  drawLogo(ctx, src, adjust, size)
  return canvas
}

function toBlob(canvas: HTMLCanvasElement): Promise<Blob | null> {
  return new Promise(resolve => canvas.toBlob(b => resolve(b), 'image/png'))
}

/**
 * Render the framing to an upload-ready square PNG. Steps the output edge down
 * rather than failing when a busy source encodes past the API's cap — a smaller
 * logo beats a 413 at save time, and every surface renders it at ≤48px anyway.
 */
export async function composeLogo(
  src: LogoSource,
  adjust: LogoAdjust,
  size = OUTPUT_SIZE,
): Promise<{ blob: Blob; dataUrl: string }> {
  const ladder = STEP_DOWN.filter(s => s <= size)
  const sizes = ladder.length ? ladder : [size]
  let last: { blob: Blob; dataUrl: string } | null = null
  for (const s of sizes) {
    const canvas = squareCanvas(src, adjust, s)
    if (!canvas) break
    const blob = await toBlob(canvas)
    if (!blob) break
    last = { blob, dataUrl: canvas.toDataURL('image/png') }
    if (blob.size <= MAX_UPLOAD_BYTES) return last
  }
  if (last) return last
  throw new Error('could not render logo')
}

// ── Brand color ──────────────────────────────────────────────────────────────

function toHex(r: number, g: number, b: number): string {
  return '#' + [r, g, b].map(x => Math.max(0, Math.min(255, x)).toString(16).padStart(2, '0')).join('')
}

/**
 * Pick a representative brand color from a drawn canvas: quantize pixels into a
 * coarse grid, weight buckets by saturation (so a vivid logo color beats a gray
 * background), and average the winning bucket. Skips transparent, near-white,
 * and near-black pixels (typically background / outlines).
 */
function dominantColor(ctx: CanvasRenderingContext2D, w: number, h: number): string {
  let data: Uint8ClampedArray
  try {
    data = ctx.getImageData(0, 0, w, h).data
  } catch {
    return FALLBACK // tainted/blank canvas
  }
  const buckets = new Map<number, { n: number; r: number; g: number; b: number; score: number }>()
  for (let i = 0; i < data.length; i += 4) {
    const a = data[i + 3]!
    if (a < 128) continue
    const r = data[i]!, g = data[i + 1]!, b = data[i + 2]!
    const max = Math.max(r, g, b), min = Math.min(r, g, b)
    if (max > 244 && min > 244) continue // near-white
    if (max < 16) continue               // near-black
    const sat = max === 0 ? 0 : (max - min) / max
    const key = ((r >> 4) << 8) | ((g >> 4) << 4) | (b >> 4)
    const cur = buckets.get(key) ?? { n: 0, r: 0, g: 0, b: 0, score: 0 }
    cur.n++; cur.r += r; cur.g += g; cur.b += b
    cur.score += 1 + sat * 2
    buckets.set(key, cur)
  }
  let best: { n: number; r: number; g: number; b: number; score: number } | null = null
  for (const v of buckets.values()) if (!best || v.score > best.score) best = v
  if (!best || best.n === 0) return FALLBACK
  return toHex(Math.round(best.r / best.n), Math.round(best.g / best.n), Math.round(best.b / best.n))
}

/**
 * Sample the brand color from the WHOLE logo (always the contain framing, on a
 * transparent matte), not from the current crop — so panning around in the
 * editor never silently rewrites a color the operator already accepted.
 */
export function detectBrandColor(src: LogoSource): string {
  const canvas = squareCanvas(src, { zoom: 1, offsetX: 0, offsetY: 0, background: 'transparent' }, 128)
  const ctx = canvas?.getContext('2d')
  if (!canvas || !ctx) return FALLBACK
  return dominantColor(ctx, canvas.width, canvas.height)
}

/**
 * One-shot convenience: load, frame with the defaults, and sample the color.
 * Used when a caller wants the "just make it work" result with no editor.
 */
export async function processLogoFile(file: File, adjust: LogoAdjust = DEFAULT_ADJUST): Promise<ProcessedLogo> {
  const src = await loadLogoSource(file)
  const { blob, dataUrl } = await composeLogo(src, adjust)
  return { blob, dataUrl, brandColor: detectBrandColor(src) }
}
